<!-- Component can be used in microscope and periscope -->
<template>
  <section class="conversation-messages">
    <template v-if="conversation">
      <header class="conversation-header">
        <div class="conversation-header-main">
          <h2 class="conversation-subject">{{ conversation.subject }}</h2>
          <div v-if="referenceLabel" class="conversation-meta">
            <span class="conversation-reference">{{ referenceLabel }}</span>
          </div>
        </div>

        <div v-if="showStatusAction" class="conversation-header-actions">
          <AppChip :color="conversation.status === 'open' ? 'success' : undefined">
            {{ statusLabel }}
          </AppChip>
          <AppButton small @click="emit('toggle-status')">
            {{ statusActionLabel }}
          </AppButton>
        </div>
      </header>

      <div
        ref="messageListRef"
        class="message-list"
        role="list"
        aria-label="Meddelanden"
        tabindex="0"
        @scroll="onMessageListScroll"
      >
        <ConversationMessage v-if="helpText" :help-text="helpText" />
        <ConversationMessage
          v-for="item in resolvedMessageItems"
          :key="item.message.id"
          :message="item.message"
          :alignment="item.alignment"
          :reply-to-message="item.replyToMessage"
          :can-reply="item.canReply"
          :can-edit="item.canEdit"
          :can-delete="item.canDelete"
          :can-mark-read="item.canMarkRead"
          @reply="startReply($event)"
          @edit="startEdit($event)"
          @delete="emit('delete', $event)"
          @mark-read="emit('mark-read', $event)"
        />
      </div>

      <div v-if="showNewMessagesIndicator" class="new-messages-indicator">
        <AppButton small color="blue" @click="scrollToLatestMessages">Nya meddelanden</AppButton>
      </div>

      <footer class="composer">
        <div v-if="replyingToMessage" class="replying-to">
          <div class="replying-to-content">
            <span class="replying-to-label">Svarar på</span>
            <p class="replying-to-message">{{ replyingToPreview }}</p>
          </div>

          <AppButton type="button" color="transparent" small @click="cancelComposerAction">Avbryt</AppButton>
        </div>

        <AppTextArea
          :value="composerValue"
          :model-value="composerValue"
          :placeholder="composerPlaceholder"
          :disabled="composerDisabled"
          :rows="4"
          @input="onInput"
          @update:model-value="onInput"
          @keydown="onKeyDown"
        />

        <div class="composer-actions">
          <AppButton color="blue" :disabled="submitDisabled" :loading="isSending" @click="handleSubmit">
            {{ sendLabel }}
          </AppButton>
        </div>
      </footer>
    </template>

    <div v-else class="empty-state">
      <h2 class="empty-state-title">{{ emptyStateTitle }}</h2>
      <p class="empty-state-body">{{ emptyStateBody }}</p>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import type { Conversation, ConversationMessage as ConversationMessageType } from '@staff/shared'

import AppButton from '../AppButton/AppButton.vue'
import AppChip from '../AppChip/AppChip.vue'
import AppTextArea from '../AppTextArea/AppTextArea.vue'
import ConversationMessage from './ConversationMessage.vue'

type ConversationMessageItem = {
  message: ConversationMessageType
  alignment?: 'sent' | 'received'
  replyToMessage?: ConversationMessageType | null
  canReply?: boolean
  canEdit?: boolean
  canDelete?: boolean
  canMarkRead?: boolean
}

export type SubmitMessagePayload = {
  message: string
  inReplyToMessageId?: string
  editMessageId?: string
}

const props = withDefaults(
  defineProps<{
    conversation?: Conversation | null
    messageItems?: ConversationMessageItem[]
    helpText?: string
    isSending?: boolean
    emptyStateTitle?: string
    emptyStateBody?: string
  }>(),
  {
    conversation: null,
    messageItems: undefined,
    helpText: undefined,
    isSending: false,
    emptyStateTitle: 'Välj en konversation',
    emptyStateBody: 'Här visas meddelanden för den valda konversationen.',
  },
)

const emit = defineEmits<{
  (e: 'submit-message', payload: SubmitMessagePayload): void
  (e: 'delete', message: ConversationMessageType): void
  (e: 'mark-read', message: ConversationMessageType): void
  (e: 'toggle-status'): void
}>()

const SCROLL_BOTTOM_THRESHOLD_PX = 80

const messageListRef = ref<HTMLElement | null>(null)
const showNewMessagesIndicator = ref(false)
const pendingForceScroll = ref(false)

// --- Composer state (owned by this component) ---

const composerValue = ref('')
const replyingToMessage = ref<ConversationMessageType | null>(null)
const editingMessage = ref<ConversationMessageType | null>(null)

const composerDisabled = computed(() => !props.conversation || props.conversation.status === 'closed')

const composerPlaceholder = computed(() => {
  if (!props.conversation) return 'Välj en konversation för att skriva ett meddelande...'
  if (props.conversation.status === 'closed') return 'Konversationen är stängd.'
  if (replyingToMessage.value) return 'Skriv ditt svar...'
  return 'Skriv ett meddelande...'
})

const sendLabel = computed(() => (editingMessage.value ? 'Spara ändring' : 'Skicka'))

const referenceLabel = computed(() => {
  if (!props.conversation) return undefined
  if (props.conversation.applicationId) {
    return `Ansökan ${props.conversation.applicationId.slice(0, 8)}`
  }
  return undefined
})

const statusActionLabel = computed(() => {
  if (!props.conversation) return undefined
  return props.conversation.status === 'open' ? 'Stäng konversation' : 'Öppna igen'
})

const statusLabel = computed(() => (props.conversation?.status === 'closed' ? 'Stängd' : 'Öppen'))

const resolvedMessageItems = computed<ConversationMessageItem[]>(() => {
  if (props.messageItems) return props.messageItems

  return (props.conversation?.messages ?? []).map((message) => ({
    message,
    alignment: 'received',
  }))
})

const replyingToPreview = computed(() => {
  if (!replyingToMessage.value) return ''
  if (replyingToMessage.value.deletedAt) return 'Meddelandet har tagits bort'
  return replyingToMessage.value.message
})

const showStatusAction = computed(() => !!props.conversation && !!statusActionLabel.value)

const submitDisabled = computed(
  () => composerDisabled.value || props.isSending || composerValue.value.trim().length === 0,
)

// --- Composer actions ---

const cancelComposerAction = () => {
  replyingToMessage.value = null
  editingMessage.value = null
  composerValue.value = ''
}

const startReply = (message: ConversationMessageType) => {
  editingMessage.value = null
  replyingToMessage.value = message
  composerValue.value = ''
}

const startEdit = (message: ConversationMessageType) => {
  replyingToMessage.value = null
  editingMessage.value = message
  composerValue.value = message.message
}

const handleSubmit = () => {
  const trimmedMessage = composerValue.value.trim()
  if (!trimmedMessage || composerDisabled.value) return

  const payload: SubmitMessagePayload = { message: trimmedMessage }

  if (editingMessage.value) {
    payload.editMessageId = editingMessage.value.id
  } else if (replyingToMessage.value) {
    payload.inReplyToMessageId = replyingToMessage.value.id
  }

  emit('submit-message', payload)
  cancelComposerAction()

  if (!payload.editMessageId) {
    pendingForceScroll.value = true
  }
}

// --- Scroll ---

const isNearBottom = () => {
  const listElement = messageListRef.value
  if (!listElement) return true

  return listElement.scrollHeight - listElement.scrollTop - listElement.clientHeight <= SCROLL_BOTTOM_THRESHOLD_PX
}

const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
  const listElement = messageListRef.value
  if (!listElement) return

  listElement.scrollTo({
    top: listElement.scrollHeight,
    behavior,
  })
}

const scrollToLatestMessages = () => {
  showNewMessagesIndicator.value = false
  scrollToBottom()
}

const onInput = (value: string) => {
  composerValue.value = value
}

const onKeyDown = (event: KeyboardEvent) => {
  if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && !submitDisabled.value) {
    event.preventDefault()
    handleSubmit()
  }
}

const onMessageListScroll = () => {
  if (isNearBottom()) {
    showNewMessagesIndicator.value = false
  }
}

// Reset composer state when conversation changes
watch(
  () => props.conversation?.id,
  async () => {
    cancelComposerAction()
    showNewMessagesIndicator.value = false
    pendingForceScroll.value = false
    await nextTick()
    if (resolvedMessageItems.value.length) {
      scrollToBottom('auto')
    } else {
      messageListRef.value?.scrollTo({ top: 0, behavior: 'auto' })
    }
  },
)

watch(
  () => {
    const lastMessage = resolvedMessageItems.value[resolvedMessageItems.value.length - 1]?.message

    return {
      conversationId: props.conversation?.id ?? null,
      messageCount: resolvedMessageItems.value.length,
      lastMessageId: lastMessage?.id ?? null,
    }
  },
  async (nextValue, previousValue) => {
    if (!nextValue.conversationId || !previousValue || nextValue.conversationId !== previousValue.conversationId) {
      return
    }

    const hasNewMessages =
      nextValue.messageCount > previousValue.messageCount ||
      (!!nextValue.lastMessageId && nextValue.lastMessageId !== previousValue.lastMessageId)

    if (!hasNewMessages) {
      return
    }

    await nextTick()

    if (pendingForceScroll.value || isNearBottom()) {
      scrollToBottom()
      showNewMessagesIndicator.value = false
    } else {
      showNewMessagesIndicator.value = true
    }

    pendingForceScroll.value = false
  },
)
</script>

<style scoped lang="scss">
.conversation-messages {
  display: grid;
  grid-template-rows: auto 1fr auto;
  min-height: 0;
  height: 100%;
  gap: 16px;
}

.conversation-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.conversation-header-main {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.conversation-subject {
  margin: 0;
  font-size: 1.125rem;
  line-height: 1.3;
}

.conversation-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  color: var(--color-fg-subtle);
  font-size: 0.875rem;
}

.conversation-header-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
}

.message-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  overflow-y: auto;
  padding: 4px;
}

.new-messages-indicator {
  display: flex;
  justify-content: center;
}

.composer {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.composer-actions {
  display: flex;
  justify-content: flex-end;
}

.replying-to {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  background-color: var(--color-canvas-subtle);
}

.replying-to-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.replying-to-label {
  font-size: 0.75rem;
  color: var(--color-fg-subtle);
}

.replying-to-message {
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.replying-to-clear {
  padding: 0;
  border: none;
  background: none;
  color: var(--color-fg-subtle);
  cursor: pointer;
  font: inherit;
}

.empty-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  padding: 24px;
}

.empty-state-title,
.empty-state-body {
  margin: 0;
}

.empty-state-body {
  color: var(--color-fg-subtle);
}
</style>
